import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AI_PDF_CONST from "../../util/PdfAiConst";
import PageHeader from "../../components/admin/PageHeader";
import useAuth from "../../hooks/useAuth";
import { isPlatformAdmin } from "../../util/accessControl";
import { fetchTenants } from "../../api/tenantApi";
import { fetchTenantUsers } from "../../api/tenantUserApi";
import { fetchTenantServices } from "../../api/tenantServiceApi";
import { fetchOnboardingStatus } from "../../api/onboardingApi";
import { fetchTenantAuditLogs, fetchAuditLogs } from "../../api/auditApi";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const formatRole = (role) => {
  const map = {
    platform_admin: "Platform Admin",
    tenant_admin: "Tenant Admin",
    support_agent: "Support Agent",
    viewer: "Viewer",
    system: "System",
  };
  return map[role] || role || "System";
};

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const role = isPlatformAdmin(user) ? "platform_admin" : user?.role || "viewer";
  const tenantId = user?.tenantId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cards, setCards] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [serviceAdoption, setServiceAdoption] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [tenantNameMap, setTenantNameMap] = useState({});

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (role === "platform_admin") {
          const tenantResponse = await fetchTenants();
          const tenantList = Array.isArray(tenantResponse)
            ? tenantResponse
            : tenantResponse?.data?.items || tenantResponse?.data || [];

          const total = tenantList.length;
          const active = tenantList.filter((t) => t.status?.toLowerCase() === "active").length;
          const onboarding = tenantList.filter((t) => t.status?.toLowerCase() === "onboarding").length;
          const suspended = tenantList.filter((t) => t.status?.toLowerCase() === "suspended").length;

          setCards([
            { label: "Total tenants", value: total, trend: "", trendBg: "" },
            { label: "Active tenants", value: active, trend: "", trendBg: "" },
            { label: "Onboarding", value: onboarding, trend: "", trendBg: "" },
            { label: "Suspended", value: suspended, trend: "", trendBg: "" },
          ]);

          const tenantIds = tenantList.map((t) => t.id);
          setTenantNameMap(Object.fromEntries(tenantList.map((t) => [String(t.id), t.name])));

          const onboardingResults = await Promise.allSettled(
            tenantIds.map((id) => fetchOnboardingStatus(id))
          );

          const STEP_LABELS = {
            business_details: "Business details",
            services: "Services",
            business_config: "Business config",
            knowledge_base: "Knowledge base",
            test: "Test",
            activate: "Activate",
          };
          const STEP_KEYS = ["business_details", "services", "business_config", "knowledge_base", "test", "activate"];
          const stepCounts = {};
          STEP_KEYS.forEach((k) => { stepCounts[k] = { completed: 0, total: 0 }; });

          onboardingResults.forEach((result) => {
            if (result.status !== "fulfilled") return;
            const data = result.value?.data || result.value || null;
            const steps = data?.steps || [];
            steps.forEach((s) => {
              const key = s.step_key || s.key;
              if (!stepCounts[key]) return;
              stepCounts[key].total += 1;
              if (s.status === "completed") stepCounts[key].completed += 1;
            });
          });

          const pipelineColors = ["bg-teal-500", "bg-blue-600", "bg-violet-500", "bg-orange-500", "bg-pink-500", "bg-emerald-500"];
          setPipeline(
            STEP_KEYS.map((key, i) => {
              const { completed, total: t } = stepCounts[key];
              return {
                label: STEP_LABELS[key],
                value: t > 0 ? Math.round((completed / t) * 100) : 0,
                color: pipelineColors[i % pipelineColors.length],
              };
            })
          );

          const serviceResults = await Promise.allSettled(
            tenantIds.map((id) => fetchTenantServices(id))
          );

          const SERVICE_LABELS = {
            customer_support: "Chat support",
            voice_support: "Voice support",
          };
          const SERVICE_COLORS = {
            customer_support: "bg-teal-50 text-teal-700",
            voice_support: "bg-purple-50 text-purple-700",
          };
          const serviceCounts = {};
          Object.keys(SERVICE_LABELS).forEach((k) => { serviceCounts[k] = 0; });

          serviceResults.forEach((result) => {
            if (result.status !== "fulfilled") return;
            const list = Array.isArray(result.value) ? result.value : result.value?.data?.items || result.value?.data || [];
            list.forEach((s) => {
              const key = s.service_key || s.key;
              if (key && serviceCounts[key] !== undefined && s.status === "enabled") {
                serviceCounts[key] += 1;
              }
            });
          });

          setServiceAdoption(
            Object.entries(serviceCounts).map(([key, count]) => ({
              label: SERVICE_LABELS[key],
              count,
              bg: SERVICE_COLORS[key] || "bg-slate-50 text-slate-700",
            }))
          );

          try {
            const logResponse = await fetchAuditLogs();
            const logs = Array.isArray(logResponse)
              ? logResponse
              : logResponse?.data?.items || logResponse?.data || [];
            setAuditLogs(logs.slice(0, 5));
          } catch {
            setAuditLogs([]);
          }
        } else if (tenantId) {
          const [usersResult, servicesResult, onboardingResult, auditResult] = await Promise.allSettled([
            fetchTenantUsers(tenantId),
            fetchTenantServices(tenantId),
            fetchOnboardingStatus(tenantId),
            fetchTenantAuditLogs(tenantId),
          ]);

          const userList = usersResult.status === "fulfilled"
            ? (Array.isArray(usersResult.value) ? usersResult.value : usersResult.value?.data?.items || usersResult.value?.data || [])
            : [];
          const serviceList = servicesResult.status === "fulfilled"
            ? (Array.isArray(servicesResult.value) ? servicesResult.value : servicesResult.value?.data?.items || servicesResult.value?.data || [])
            : [];
          const onboardingData = onboardingResult.status === "fulfilled"
            ? (onboardingResult.value?.data || onboardingResult.value || null)
            : null;
          const auditList = auditResult.status === "fulfilled"
            ? (Array.isArray(auditResult.value) ? auditResult.value : auditResult.value?.data?.items || auditResult.value?.data || [])
            : [];

          const totalUsers = userList.length;
          const activeUsers = userList.filter((u) => u.status === "active").length;
          const invitedUsers = userList.filter((u) => u.status === "invited").length;
          const disabledUsers = userList.filter((u) => u.status === "inactive").length;
          const onboardingProgress = onboardingData?.completed ? 100 : (onboardingData?.steps?.filter((s) => s.status === "completed").length || 0);
          const onboardingTotal = onboardingData?.steps?.length || 4;
          const progressPercent = onboardingTotal > 0 ? Math.round((onboardingProgress / onboardingTotal) * 100) : 0;

          const pendingLabel = invitedUsers > 0 ? `Pending Invites (${invitedUsers})` : "Disabled Users";

          setCards([
            { label: "Total users", value: totalUsers, trend: "", trendBg: "" },
            { label: "Active users", value: activeUsers, trend: "", trendBg: "" },
            { label: "Onboarding progress", value: `${progressPercent}%`, trend: "", trendBg: "" },
            { label: pendingLabel, value: invitedUsers > 0 ? invitedUsers : disabledUsers, trend: "", trendBg: "" },
          ]);

          const steps = onboardingData?.steps || [];
          const stepColors = ["bg-teal-500", "bg-blue-600", "bg-violet-500", "bg-orange-500"];
          setPipeline(steps.map((s, i) => ({
            label: s.label || s.key,
            value: s.status === "completed" ? 100 : s.status === "in_progress" ? 50 : 0,
            color: stepColors[i % stepColors.length],
          })));

          setServiceAdoption(serviceList
            .filter((s) => !["document_rag", "appointment_booking"].includes(s.service_key || s.key))
            .map((s) => ({
            label: s.service_key || s.key,
            enabled: s.status === "enabled",
            bg: s.status === "enabled" ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500",
          })));

          setAuditLogs(auditList.slice(0, 5));
        }
      } catch (err) {
        // console.error("Dashboard data load failed:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [role, tenantId]);

  if (loading) {
    return (
      <div className="space-y-6 pb-12 animate-pulse">
        <div className="h-12 w-1/3 bg-slate-200 rounded-xl"></div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-slate-100 rounded-2xl border border-slate-200"></div>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 h-48 bg-slate-100 rounded-2xl border border-slate-200"></div>
          <div className="h-48 bg-slate-100 rounded-2xl border border-slate-200"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-center shadow-sm">
        <h3 className="text-sm font-bold text-rose-800">Connection Interrupted</h3>
        <p className="mt-2 text-xs text-rose-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded-xl bg-rose-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-rose-700"
        >
          Retry Sync
        </button>
      </div>
    );
  }

  const dashboardTitle = role === "platform_admin" ? "Doclift Dashbaord" : "Tenant dashboard";
  const dashboardDescription = role === "platform_admin"
    ? "Manage and monitor all your tenants."
    : "Team overview, onboarding progress, service status, and recent activity.";

  return (
    <div className="space-y-6 pb-12">
      {!isPlatformAdmin(user) ? (
        <div className="rounded-2xl border border-slate-100 bg-white px-6 py-5 shadow-sm">
          <h1 className="text-xl font-bold text-slate-950">Welcome! {user.name}</h1>
          <p className="mt-0.5 text-sm text-slate-500">Here's what's happening with your workspace</p>
        </div>
      ) : (
        <PageHeader title={dashboardTitle} description={dashboardDescription} />
      )}

      {/* 1. Metric Cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((stat, idx) => (
          <div key={stat.label || `stat-${idx}`} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md hover:border-slate-200 cursor-default">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{stat.label}</p>
            <h3 className="mt-2 text-3xl font-bold text-slate-900 transition-colors duration-200 group-hover:text-[#096dea]">{stat.value}</h3>
            {stat.trend ? (
              <span className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${stat.trendBg}`}>
                {stat.trend}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {/* 2. Pipeline & Service Adoption */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Onboarding Pipeline */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-950">
                {role === "platform_admin" ? "Onboarding pipeline" : "Onboarding progress"}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {role === "platform_admin"
                  ? "Track every tenant from draft to activation."
                  : "Your tenant's onboarding steps."}
              </p>
            </div>
            {role === "platform_admin" ? (
              <Link to={AI_PDF_CONST.ROUTE.ADMIN_TENANTS} className="text-xs font-semibold text-[#096dea] hover:underline">
                View all
              </Link>
            ) : null}
          </div>

          <div className="mt-6 space-y-4">
            {pipeline.length > 0 ? pipeline.map((item, idx) => (
              <div key={item.label || item.key || `pipeline-${idx}`} className="grid grid-cols-12 items-center gap-4">
                <span className="col-span-4 text-xs font-medium text-slate-700">{item.label}</span>
                <div className="col-span-6 h-2.5 w-full rounded-full bg-slate-100">
                  <div className={`h-2.5 rounded-full ${item.color}`} style={{ width: `${item.value}%` }}></div>
                </div>
                <span className="col-span-2 text-right text-xs font-semibold text-slate-500">{item.value}%</span>
              </div>
            )) : (
              <p className="text-xs text-slate-400">No onboarding data available.</p>
            )}
          </div>
        </div>

        {/* Service Adoption */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-950">Service adoption</h3>
            <p className="text-xs text-slate-500 mt-1 mb-4">
              {role === "platform_admin" ? "Active tracking of feature usage." : "Your tenant's service status."}
            </p>

            <div className="space-y-3">
              {serviceAdoption.length > 0 ? serviceAdoption.map((service, idx) => (
                <div key={service.label || service.key || `service-${idx}`} className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-1 rounded-md font-medium ${service.bg}`}>
                    {service.label}
                  </span>
                  <span className="text-slate-500 font-semibold">
                    {role === "platform_admin"
                      ? `${service.count} tenants`
                      : service.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              )) : (
                <p className="text-xs text-slate-400">No services configured.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Audit Activity */}
      <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-950">Recent audit activity</h3>
        </div>
        <div className="overflow-x-auto">
          {auditLogs.length === 0 ? (
            <div className="p-8 text-center text-xs font-medium text-slate-400">
              No recent audit activity found.
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Action</th>
                  {role === "platform_admin" ? <th className="px-6 py-3">Tenant</th> : null}
                  <th className="px-6 py-3">Actor</th>
                  <th className="px-6 py-3">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-900">
                {auditLogs.map((log, idx) => (
                  <tr key={log.id || log._id || `${log.timestamp || log.created_at || "log"}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-slate-950">{log.action || "event.logged"}</td>
                    {role === "platform_admin" ? (
                      <td className="px-6 py-4 text-slate-600">{log.tenant_name || log.tenant || tenantNameMap?.[log.tenant_id] || log.tenant_id || "System"}</td>
                    ) : null}
                    <td className="px-6 py-4 text-slate-600">{log.actor && isNaN(log.actor) ? log.actor : formatRole(log.actor_role)}</td>
                    <td className="px-6 py-4 text-slate-500">{formatDateTime(log.created_at || log.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
